import styled from '@emotion/styled'
import { capitalize } from 'lodash'
import { PropsWithChildren, useState } from 'react'

import { ISO_DATE_FORMAT } from '@bemlo/date'
import { ScheduleShift } from '@bemlo/schedule'

import { ShiftPreset } from './composable'
import { Color, theme } from '../../constants'
import {
  Badge,
  Button,
  Clickable,
  CloseButtonDialogBody,
  Dialog,
  Flex,
  HorizontalRule,
  Icon,
  Table,
  Text,
  withDialog,
} from '../../elements'
import { DatePicker } from '../../elements/Form/DatePicker'
import { DigitInput } from '../../elements/Form/DigitInput'
import { useZod } from '../../hooks'
import { useTranslations } from '../../translate'

const StyledCell = styled(Table.Cell)`
  padding: 0;
`

const StyledHeaderCell = styled(Table.HeadCell)`
  padding: 0;
  text-align: center;
`

const IncrementerHover = styled(Flex)`
  padding: ${theme.spacing[1]}px 0;
  button {
    transition: opacity 0.1s ease-in-out;
    opacity: 0;
  }
  justify-content: space-around;
  &:hover {
    button {
      opacity: 1;
    }
  }
`

type IncrementerProps = PropsWithChildren<{
  onIncrement: () => void
  onDecrement: () => void
  incrementDisabled?: boolean
  decrementDisabled?: boolean
}>

const Incrementer = ({
  children,
  onIncrement,
  incrementDisabled,
  onDecrement,
  decrementDisabled,
}: IncrementerProps) => {
  return (
    <IncrementerHover gap={0.5}>
      <Clickable
        onClick={onDecrement}
        disabled={decrementDisabled}
        styles={{ width: 'fit-content' }}
      >
        <Icon
          color={Color.DARKEST_MEDIUM_GREY}
          size="sm"
          name="MinusCircleIcon"
        />
      </Clickable>
      {children}
      <Clickable
        onClick={onIncrement}
        disabled={incrementDisabled}
        styles={{ width: 'fit-content' }}
      >
        <Icon
          size="sm"
          color={Color.DARKEST_MEDIUM_GREY}
          name="PlusCircleIcon"
        />
      </Clickable>
    </IncrementerHover>
  )
}

const DAY_INDICES = Array(7)
  .fill(0)
  .map((_, index) => index)

const Row = ({
  variant,
  values,
  setValues,
}: {
  variant: ShiftPreset
  values: number[]
  setValues: (shiftVariantId: string, newValues: number[]) => void
}) => {
  const increment = (dayIndex: number) => {
    const newValues = [...values]
    newValues[dayIndex] += 1
    setValues(variant.key, newValues)
  }

  const decrement = (dayIndex: number) => {
    const newValues = [...values]
    newValues[dayIndex] = Math.max(0, newValues[dayIndex] - 1)
    setValues(variant.key, newValues)
  }

  const incrementAll = () => {
    const newValues = values.map((value) => value + 1)
    setValues(variant.key, newValues)
  }

  const decrementAll = () => {
    const newValues = values.map((value) => Math.max(0, value - 1))
    setValues(variant.key, newValues)
  }

  const decrementDisabled = values.every((value) => value <= 0)

  return (
    <Table.Row>
      <StyledCell>
        <Incrementer
          onIncrement={incrementAll}
          onDecrement={decrementAll}
          decrementDisabled={decrementDisabled}
        >
          <Text size={6.5} fontWeight={theme.font.weight.bold}>
            {variant.startTime} - {variant.endTime}
          </Text>
        </Incrementer>
      </StyledCell>
      {DAY_INDICES.map((index) => (
        <StyledCell key={index}>
          <Incrementer
            onIncrement={() => increment(index)}
            onDecrement={() => decrement(index)}
            decrementDisabled={values[index] === 0}
          >
            <Badge size="xs">{values[index]}</Badge>
          </Incrementer>
        </StyledCell>
      ))}
    </Table.Row>
  )
}

type ShiftWithComment = ScheduleShift & { comment: string }

export type BulkAddShiftsDialogProps = {
  shiftVariants: ShiftPreset[]
  onAddShifts: (shifts: ShiftWithComment[]) => void
  periodStart: number
  periodEnd: number
}

export const BulkAddShiftsDialog = withDialog<BulkAddShiftsDialogProps>(
  ({ shiftVariants, onAddShifts, periodStart, periodEnd, onClose }) => {
    const t = useTranslations()
    const startOfTenderStartOfDay = t.$date.unix(periodStart).startOf('day')
    const endOfTenderEndOfDay = t.$date.unix(periodEnd).endOf('day')
    const [forceShowErrors, setForceShowErrors] = useState(false)
    const [start, setStart] = useState<Date>(startOfTenderStartOfDay.toDate())
    const [periodicity, setPeriodicity] = useState<number | undefined>(1)
    const [shiftVariantsToAdd, setShiftVariantsToAdd] = useState<
      Record<string, number[]>
    >(
      shiftVariants.reduce(
        (acc, shiftVariant) => ({
          ...acc,
          [shiftVariant.key]: Array(7).fill(0),
        }),
        {},
      ),
    )

    const updateShiftsToAdd = (shiftVariantId: string, newValues: number[]) => {
      setShiftVariantsToAdd((prev) => ({
        ...prev,
        [shiftVariantId]: newValues,
      }))
    }

    const z = useZod()
    const schema = z.object({
      periodicity: z.number().int().positive(),
      periodStart: z
        .date()
        .min(startOfTenderStartOfDay.toDate())
        .max(endOfTenderEndOfDay.toDate()),
    })

    const onSubmit = () => {
      const results = schema.safeParse({ periodicity, periodStart: start })
      if (!results.success) {
        setForceShowErrors(true)
        return
      }

      const periodStartWeekDayJs = t.$date(results.data.periodStart).weekday(0)
      const newShifts: ShiftWithComment[] = []

      let currentDay = t.$date(results.data.periodStart)
      while (currentDay.isBefore(endOfTenderEndOfDay)) {
        const dayIndex = currentDay.weekday()
        if (dayIndex === 0) {
          const weekDiff = currentDay.diff(periodStartWeekDayJs, 'week')
          if (weekDiff % results.data.periodicity !== 0) {
            currentDay = currentDay.add(1, 'week')
            continue
          }
        }
        shiftVariants.forEach((shiftVariant) => {
          const value = shiftVariantsToAdd[shiftVariant.key][dayIndex]
          if (value > 0) {
            for (let i = 0; i < value; i++) {
              newShifts.push({
                ...shiftVariant,
                date: currentDay.format(ISO_DATE_FORMAT),
              })
            }
          }
        })
        currentDay = currentDay.add(1, 'day')
      }
      onAddShifts(newShifts)
      onClose()
    }

    return (
      <CloseButtonDialogBody>
        <Dialog.Title>{t.shifts.add_many()}</Dialog.Title>
        <Flex gap={2}>
          <DatePicker
            schema={schema.shape.periodStart}
            reserveErrorSpace
            forceShowError={forceShowErrors}
            required
            minDate={startOfTenderStartOfDay.toDate()}
            maxDate={endOfTenderEndOfDay.toDate()}
            value={start}
            label={t.shifts.add_many_period_start()}
            onChange={(value) => value && setStart(value)}
          />
          <DigitInput
            schema={schema.shape.periodicity}
            reserveErrorSpace
            forceShowError={forceShowErrors}
            required
            value={periodicity}
            onChange={setPeriodicity}
            label={t.shifts.add_many_periodicity()}
            placeholder={t.shifts.add_many_periodicity_placeholder()}
          />
        </Flex>
        <Text>
          {periodicity && start ? (
            t.shifts.add_many_description(
              periodicity,
              t.$date(start).format(ISO_DATE_FORMAT),
            )
          ) : (
            <>&nbsp;</>
          )}
        </Text>
        <HorizontalRule my={3} />
        <Table borderless>
          <Table.Head>
            <StyledHeaderCell />
            {DAY_INDICES.map((index) => (
              <StyledHeaderCell key={index}>
                {capitalize(t.$date().weekday(index).format('ddd'))}
              </StyledHeaderCell>
            ))}
          </Table.Head>
          <Table.Body>
            {shiftVariants.map((shiftVariant) => (
              <Row
                key={shiftVariant.key}
                variant={shiftVariant}
                values={shiftVariantsToAdd[shiftVariant.key]}
                setValues={updateShiftsToAdd}
              />
            ))}
          </Table.Body>
        </Table>
        <HorizontalRule
          styles={{
            marginTop: theme.spacing[6.5],
            marginBottom: theme.spacing[3],
          }}
        />
        <Flex gap={2}>
          <Button size="sm" onClick={onSubmit}>
            {t.ui.confirm()}
          </Button>
          <Button size="sm" onClick={onClose} design="danger-inverted">
            {t.ui.cancel()}
          </Button>
        </Flex>
      </CloseButtonDialogBody>
    )
  },
)
